import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Loader2, CheckCircle2, Sparkles } from "lucide-react";
import { submitHostingLead } from "@/lib/hosting-leads.functions";

type Props = {
  slug: string;
  planName?: string;
  trigger?: React.ReactNode;
  title?: string;
  description?: string;
};

export function HostingQuoteDialog({
  slug,
  planName,
  trigger,
  title = "Request a custom quote",
  description = "Tell us about your workload and we'll get back within one business day.",
}: Props) {
  const [open, setOpen] = useState(false);
  const [done, setDone] = useState(false);
  const submit = useServerFn(submitHostingLead);

  const mutate = useMutation({
    mutationFn: async (values: Record<string, any>) => submit({ data: values as any }),
    onSuccess: () => {
      setDone(true);
      toast.success("Quote request sent — we'll reach out shortly.");
    },
    onError: (e: any) => toast.error(e.message ?? "Could not send request"),
  });

  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    const budgetRaw = String(f.get("budget") ?? "").trim();
    const budgetCents = budgetRaw ? Math.round(Number(budgetRaw) * 100) : null;
    mutate.mutate({
      page_slug: slug,
      name: String(f.get("name") ?? ""),
      email: String(f.get("email") ?? ""),
      company: String(f.get("company") ?? ""),
      phone: String(f.get("phone") ?? ""),
      plan_interest: String(f.get("plan_interest") ?? planName ?? ""),
      message: String(f.get("message") ?? ""),
      budget_monthly_cents: Number.isFinite(budgetCents) ? budgetCents : null,
      timeline: String(f.get("timeline") ?? ""),
      source: typeof window !== "undefined" ? window.location.pathname : "",
      user_agent: typeof navigator !== "undefined" ? navigator.userAgent.slice(0, 500) : "",
    });
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        setOpen(v);
        if (!v) setDone(false);
      }}
    >
      <DialogTrigger asChild>
        {trigger ?? (
          <Button size="lg" className="gap-2">
            <Sparkles className="h-4 w-4" /> Get a custom quote
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        {done ? (
          <div className="flex flex-col items-center gap-3 py-6 text-center">
            <CheckCircle2 className="h-10 w-10 text-emerald-500" />
            <div className="text-sm font-medium">Request received</div>
            <p className="text-xs text-muted-foreground">
              Our infrastructure team will email you shortly with a tailored proposal.
            </p>
            <Button variant="outline" size="sm" onClick={() => setOpen(false)}>Close</Button>
          </div>
        ) : (
          <form onSubmit={onSubmit} className="grid gap-3">
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="Full name *" name="name" required maxLength={120} autoComplete="name" />
              <Field label="Work email *" name="email" type="email" required maxLength={255} autoComplete="email" />
              <Field label="Company" name="company" maxLength={160} autoComplete="organization" />
              <Field label="Phone" name="phone" type="tel" maxLength={60} autoComplete="tel" />
              <Field label="Plan of interest" name="plan_interest" defaultValue={planName ?? ""} maxLength={160} />
              <Field label="Budget / month (USD)" name="budget" type="number" min={0} step={1} inputMode="numeric" />
              <Field label="Timeline" name="timeline" placeholder="e.g. within 2 weeks" maxLength={120} className="sm:col-span-2" />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="hosting-lead-message" className="text-xs">Requirements</Label>
              <Textarea
                id="hosting-lead-message"
                name="message"
                rows={4}
                maxLength={4000}
                placeholder="Workload, expected traffic, storage / GPU needs, compliance requirements…"
              />
            </div>
            <div className="mt-2 flex items-center justify-end gap-2">
              <Button type="button" variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={mutate.isPending}>
                {mutate.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Send request
              </Button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Field({
  label,
  className,
  ...props
}: React.InputHTMLAttributes<HTMLInputElement> & { label: string; className?: string }) {
  const id = `hlead-${props.name}`;
  return (
    <div className={`grid gap-1.5 ${className ?? ""}`}>
      <Label htmlFor={id} className="text-xs">{label}</Label>
      <Input id={id} {...props} />
    </div>
  );
}
